import { isSupportedCssColor } from './cssColor';

export type ColorTokenName = `--fett-${string}`;
export type ColorTokenMode = 'light' | 'dark';
export type ColorTokenStatus = 'stable' | 'provisional';
export type ColorTokenCategory = 'background' | 'text' | 'border' | 'fill' | 'stroke';

export interface ColorTokenDefinition {
  light: string;
  dark: string;
  status: ColorTokenStatus;
  category: ColorTokenCategory;
  description: string;
}

export interface ColorTokenRecord extends ColorTokenDefinition {
  name: ColorTokenName;
}

export interface ColorTokenRegistryValidation {
  valid: boolean;
  errors: string[];
}

const stable = (
  light: string,
  dark: string,
  category: ColorTokenCategory,
  description: string,
): ColorTokenDefinition => ({ light, dark, status: 'stable', category, description });

export const colorTokenRegistry = {
  '--fett-bg-app': stable('#f0f2f5', '#0f1115', 'background', 'Application canvas background'),
  '--fett-sider-item-text': stable(
    'rgba(255, 255, 255, 0.5)',
    'rgba(220, 224, 230, 0.72)',
    'text',
    'Sidebar item text',
  ),
  '--fett-sider-item-hover-bg': stable(
    '#ffffff',
    'rgba(255, 255, 255, 0.08)',
    'background',
    'Sidebar item hover background',
  ),
  '--fett-sider-item-active-bg': stable(
    '#ffffff',
    '#3e4042',
    'background',
    'Sidebar item active background',
  ),
  '--fett-bg-sider': stable('#ffffff', '#15181c', 'background', 'Sidebar surface'),
  '--fett-bg-panel': stable('#ffffff', '#181d28', 'background', 'Standard panel surface'),
  '--fett-bg-elevated': stable('#ffffff', '#202734', 'background', 'Elevated surface'),
  '--fett-bg-subtle': stable('#f7f9fc', '#181d28', 'background', 'Subtle surface'),
  '--fett-bg-hover': stable('#eef3fb', '#343a44', 'background', 'Generic hover surface'),
  '--fett-bg-active': stable('#e9f3ff', '#263244', 'background', 'Generic active surface'),
  '--fett-bg-info-tip': stable('#F0F7FF', '#13161a', 'background', 'Info tip background'),
  '--fett-bg-input': stable('#ffffff', '#161a1f', 'background', 'Input surface'),
  '--fett-bg-mask': stable(
    'rgba(17, 24, 39, 0.45)',
    'rgba(0, 0, 0, 0.68)',
    'background',
    'Modal mask',
  ),
  '--fett-border-default': stable('#f0f2f5', '#2a2f36', 'border', 'Default border'),
  '--fett-border-strong': stable('#d3d7e0', '#3a414a', 'border', 'Strong border'),
  '--fett-border-input': stable('#d9d9d9', '#373c43', 'border', 'Form control border'),
  '--fett-text-primary': stable('#232426', '#f2f3f5', 'text', 'Primary text'),
  '--fett-text-secondary': stable('#515359', '#cfd4da', 'text', 'Secondary text'),
  '--fett-text-tertiary': stable('#84868c', '#9ca5af', 'text', 'Tertiary text'),
  '--fett-text-icon-muted': stable('#888888', '#a3abb4', 'text', 'Muted icon text'),
  '--fett-text-quaternary': stable('#b5b9bf', '#747e8a', 'text', 'Quaternary text'),
  '--fett-fill-600': stable('#464d5a', '#f2f3f5', 'fill', 'Legacy strongest neutral fill'),
  '--fett-fill-500': stable('#81868f', '#cfd4da', 'fill', 'Legacy strong neutral fill'),
  '--fett-fill-400': stable('#cbcdd1', '#9ca5af', 'fill', 'Legacy medium neutral fill'),
  '--fett-fill-300': stable('#e9ebed', '#3a414a', 'fill', 'Legacy neutral fill'),
  '--fett-fill-200': stable('#f3f5f7', '#262b32', 'fill', 'Legacy subtle neutral fill'),
  '--fett-fill-100': stable('#f9fafb', '#181b20', 'fill', 'Legacy faint neutral fill'),
  '--fett-fill-0': stable('#ffffff', '#20242a', 'fill', 'Legacy base neutral fill'),
  '--fett-streak-white': stable('#f0f2f5', '#2a2f36', 'border', 'Legacy light divider'),
  '--fett-streak-grey': stable('#e1e4eb', '#3a414a', 'border', 'Legacy strong divider'),
  '--fett-content-400': stable('#1d2129', '#f2f3f5', 'text', 'Legacy primary content'),
  '--fett-content-300': stable('#4e5969', '#cfd4da', 'text', 'Legacy secondary content'),
  '--fett-content-200': stable('#86909c', '#9ca5af', 'text', 'Legacy tertiary content'),
  '--fett-content-100': stable('#c9cdd4', '#747e8a', 'text', 'Legacy muted content'),
  '--fett-bg-light': stable(
    '#e9f3ff',
    'rgba(255, 255, 255, 0.06)',
    'background',
    'Legacy highlighted background',
  ),
  '--fett-chat-bubble-customer': stable(
    '#ffffff',
    '#23282f',
    'background',
    'Customer message bubble',
  ),
  '--fett-chat-bubble-agent': stable('#d6e8ff', '#263244', 'background', 'Agent message bubble'),
  '--fett-chat-bubble-bot-reply': stable(
    '#f6fff9',
    '#1f3329',
    'background',
    'Bot reply message bubble',
  ),
  '--fett-chat-bubble-system': stable('#f5f6f8', '#1c2026', 'background', 'System message bubble'),
  '--fett-chat-card-bg': stable('#ffffff', '#20242a', 'background', 'Conversation card surface'),
  '--fett-chat-card-border': stable('#d9e5fd', '#343b45', 'border', 'Conversation card border'),
  '--fett-chat-editor-bg': stable('#ffffff', '#10141b', 'background', 'Message editor surface'),
  '--fett-chat-stats-header-bg': stable(
    '#ffffff',
    '#10141b',
    'background',
    'Conversation statistics header surface',
  ),
  '--fett-chat-head-bg': stable('#f0f2f5', '#10141b', 'background', 'Conversation head surface'),
  '--fett-chat-window-bg': stable('#f0f2f5', '#10141b', 'background', 'Chat window surface'),
  '--fett-product-card-bg': stable('#ffffff', '#20242a', 'background', 'Product card surface'),
  '--fett-product-card-hover': stable(
    '#f7f9fc',
    '#262b32',
    'background',
    'Product card hover surface',
  ),
  '--fett-product-card-border': stable('#f0f2f5', '#343b45', 'border', 'Product card border'),
  '--fett-product-table-stripe': stable(
    '#f7f9fa',
    '#20252b',
    'background',
    'Product table striped row',
  ),
  '--fett-erp-card-bg': stable('#f7f9fa', '#20242a', 'background', 'ERP card surface'),
  '--fett-surface-warning-bg': stable(
    '#fffaf0',
    'rgba(255, 176, 32, 0.14)',
    'background',
    'Warning surface',
  ),
  '--fett-surface-warning-border': stable(
    '#faedd2',
    'rgba(255, 176, 32, 0.34)',
    'border',
    'Warning surface border',
  ),
  '--fett-surface-warning-text': stable('#f2a100', '#ffc453', 'text', 'Warning text'),
  '--fett-surface-error-bg': stable(
    '#fff7f7',
    'rgba(255, 107, 107, 0.16)',
    'background',
    'Error surface',
  ),
  '--fett-surface-error-border': stable(
    '#ffd9d9',
    'rgba(255, 107, 107, 0.36)',
    'border',
    'Error surface border',
  ),
  '--fett-surface-error-text': stable('#f05b5b', '#ff9a9a', 'text', 'Error text'),
  '--fett-surface-success-bg': stable(
    '#ebfaf3',
    'rgba(45, 194, 117, 0.16)',
    'background',
    'Success surface',
  ),
  '--fett-surface-success-border': stable(
    '#caefdb',
    'rgba(45, 194, 117, 0.34)',
    'border',
    'Success surface border',
  ),
  '--fett-surface-success-text': stable('#00aa5a', '#72d99f', 'text', 'Success text'),
  '--fett-surface-info-bg': stable(
    '#e6f7ff',
    'rgba(89, 154, 255, 0.16)',
    'background',
    'Information surface',
  ),
  '--fett-surface-info-border': stable(
    '#c8e7fb',
    'rgba(89, 154, 255, 0.34)',
    'border',
    'Information surface border',
  ),
  '--fett-surface-info-text': stable('#096dd9', '#9bc0ff', 'text', 'Information text'),
  '--fett-platform-tiktok-text': stable('#141514', '#cfd4da', 'text', 'TikTok platform tag text'),
  '--fett-platform-tiktok-border': stable(
    '#d0d0d0',
    '#3a414a',
    'border',
    'TikTok platform tag border',
  ),
  '--fett-scrollbar-thumb': stable(
    'hsla(0, 0%, 70.6%, 0.5)',
    'rgba(156, 165, 175, 0.46)',
    'fill',
    'Scrollbar thumb',
  ),
  '--fett-conv-sider-head-bg': stable(
    '#ffffff',
    '#202734',
    'background',
    'Conversation sider shop head surface',
  ),
  '--fett-conv-item-hover': stable(
    '#f7f9fc',
    '#22272e',
    'background',
    'Conversation list item hover',
  ),
  '--fett-conv-item-active': stable(
    '#ececed',
    '#263244',
    'background',
    'Conversation list item active',
  ),
  '--fett-conv-item-affiliate-active': stable(
    '#e7e9ee',
    'rgba(156, 165, 175, 0.16)',
    'background',
    'Affiliate conversation item active',
  ),
  '--fett-conv-item-remind': stable(
    '#ffe492',
    'rgba(246, 185, 59, 0.2)',
    'background',
    'Conversation reminder',
  ),
  '--fett-conv-item-remind-active': stable(
    '#fcd45c',
    'rgba(246, 185, 59, 0.25)',
    'background',
    'Active conversation reminder',
  ),
  '--fett-conv-item-unprocessed': stable(
    '#fff4eb',
    'rgba(255, 107, 107, 0.1)',
    'background',
    'Unprocessed conversation item',
  ),
  '--fett-conv-item-unprocessed-active': stable(
    '#ffe2c8',
    'rgba(255, 107, 107, 0.15)',
    'background',
    'Active unprocessed conversation item',
  ),
  '--fett-aide-background': stable(
    '#e9f3ff',
    'rgba(238, 78, 45, 0.16)',
    'background',
    'Aide selected item background',
  ),
  '--fett-aide-remind': stable('#ffab00', '#ffc453', 'fill', 'Aide reminder accent'),
  '--fett-scenario-accent-bg': stable(
    '#f0f7ff',
    'rgba(51, 111, 255, 0.16)',
    'background',
    'Scenario accent surface',
  ),
  '--fett-scenario-accent-strong-bg': stable(
    '#d6e8ff',
    'rgba(51, 111, 255, 0.3)',
    'background',
    'Scenario strong accent surface',
  ),
  '--fett-scenario-accent-text': stable('#336fff', '#8aadff', 'text', 'Scenario accent text'),
  '--fett-scenario-header-bg': stable(
    '#ecf1ff',
    'rgba(51, 111, 255, 0.14)',
    'background',
    'Scenario card header gradient start',
  ),
  '--fett-scenario-success-bg': stable(
    '#ddfbea',
    'rgba(0, 191, 105, 0.24)',
    'background',
    'Scenario success accent surface',
  ),
  '--fett-scenario-success-soft-bg': stable(
    '#f2fff7',
    'rgba(0, 191, 105, 0.12)',
    'background',
    'Scenario success gradient start',
  ),
  '--fett-scenario-success-text': stable(
    '#00bf69',
    '#4eda9b',
    'text',
    'Scenario success accent text',
  ),
  '--fett-scenario-purple-bg': stable(
    '#f4f0ff',
    'rgba(112, 92, 242, 0.18)',
    'background',
    'Scenario purple accent surface',
  ),
  '--fett-scenario-purple-text': stable(
    '#705cf2',
    '#a89aff',
    'text',
    'Scenario purple accent text',
  ),
  '--fett-scenario-orange-bg': stable(
    '#fff3e8',
    'rgba(255, 138, 76, 0.16)',
    'background',
    'Scenario orange accent surface',
  ),
  '--fett-scenario-orange-text': stable(
    '#d5390c',
    '#ff9a76',
    'text',
    'Scenario orange accent text',
  ),
  '--fett-knowledge-product-card-bg': stable(
    '#e2e9f7',
    '#262b32',
    'background',
    'Supplement knowledge product card surface',
  ),
  '--fett-knowledge-question-bubble-bg': stable(
    '#def6f6',
    '#1f3329',
    'background',
    'Supplement knowledge buyer question bubble',
  ),
  /* UED 暗色 Tag 配色：填充浅色型（bg+text，无边框）与描边型（border+text，无底）。
     light 值记录 antd 现行预设，实际覆写仅在 dark 生效，light 显示不变 */
  '--fett-tag-blue-bg': stable(
    '#e6f7ff',
    'rgba(51, 102, 255, 0.16)',
    'background',
    'Tag blue filled surface',
  ),
  '--fett-tag-blue-border': stable(
    '#91d5ff',
    'rgba(51, 102, 255, 0.45)',
    'border',
    'Tag blue outline border',
  ),
  '--fett-tag-blue-text': stable('#096dd9', '#7EA2FF', 'text', 'Tag blue text'),
  '--fett-tag-green-bg': stable(
    '#f6ffed',
    'rgba(60, 203, 127, 0.14)',
    'background',
    'Tag green filled surface',
  ),
  '--fett-tag-green-border': stable(
    '#b7eb8f',
    'rgba(60, 203, 127, 0.45)',
    'border',
    'Tag green outline border',
  ),
  '--fett-tag-green-text': stable('#389e0d', '#52D996', 'text', 'Tag green text'),
  '--fett-tag-orange-bg': stable(
    '#fff7e6',
    'rgba(246, 185, 59, 0.14)',
    'background',
    'Tag orange filled surface',
  ),
  '--fett-tag-orange-border': stable(
    '#ffd591',
    'rgba(246, 185, 59, 0.45)',
    'border',
    'Tag orange outline border',
  ),
  '--fett-tag-orange-text': stable('#d46b08', '#F6C65B', 'text', 'Tag orange text'),
  '--fett-tag-red-bg': stable(
    '#fff1f0',
    'rgba(255, 107, 107, 0.14)',
    'background',
    'Tag red filled surface',
  ),
  '--fett-tag-red-border': stable(
    '#ffa39e',
    'rgba(255, 107, 107, 0.45)',
    'border',
    'Tag red outline border',
  ),
  '--fett-tag-red-text': stable('#cf1322', '#FF8585', 'text', 'Tag red text'),
  '--fett-tag-purple-bg': stable(
    '#f9f0ff',
    'rgba(139, 124, 255, 0.14)',
    'background',
    'Tag purple filled surface',
  ),
  '--fett-tag-purple-border': stable(
    '#d3adf7',
    'rgba(139, 124, 255, 0.45)',
    'border',
    'Tag purple outline border',
  ),
  '--fett-tag-purple-text': stable('#531dab', '#A99BFF', 'text', 'Tag purple text'),
  '--fett-tag-gray-bg': stable(
    '#fafafa',
    'rgba(255, 255, 255, 0.08)',
    'background',
    'Tag gray filled surface',
  ),
  '--fett-tag-gray-border': stable(
    '#d9d9d9',
    'rgba(255, 255, 255, 0.16)',
    'border',
    'Tag gray outline border',
  ),
  '--fett-tag-gray-text': stable('rgba(0, 0, 0, 0.85)', '#A9B3C2', 'text', 'Tag gray text'),
} satisfies Record<ColorTokenName, ColorTokenDefinition>;

const TOKEN_NAME_RE = /^--fett-[a-z0-9-]+$/;
const VALID_STATUSES = new Set<ColorTokenStatus>(['stable', 'provisional']);
const VALID_CATEGORIES = new Set<ColorTokenCategory>([
  'background',
  'text',
  'border',
  'fill',
  'stroke',
]);

export function getColorToken(name: string): ColorTokenDefinition | null {
  return colorTokenRegistry[name as keyof typeof colorTokenRegistry] ?? null;
}

export function listColorTokens(): ColorTokenRecord[] {
  return Object.entries(colorTokenRegistry).map(([name, definition]) => ({
    name: name as ColorTokenName,
    ...definition,
  }));
}

export function createColorVariableMap(mode: ColorTokenMode): Record<string, string> {
  return Object.fromEntries(
    listColorTokens().map(({ name, light, dark }) => [name, mode === 'light' ? light : dark]),
  );
}

export function isColorTokenName(value: unknown): value is ColorTokenName {
  return typeof value === 'string' && TOKEN_NAME_RE.test(value);
}

export function validateColorTokenDefinition(
  value: unknown,
  path = 'definition',
): ColorTokenRegistryValidation {
  const errors: string[] = [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { valid: false, errors: [`${path}: definition must be an object`] };
  }

  const definition = value as Partial<ColorTokenDefinition>;
  if (!isSupportedCssColor(definition.light)) {
    errors.push(`${path}: light must be a supported CSS color`);
  }
  if (!isSupportedCssColor(definition.dark)) {
    errors.push(`${path}: dark must be a supported CSS color`);
  }
  if (!definition.status || !VALID_STATUSES.has(definition.status)) {
    errors.push(`${path}: status must be stable or provisional`);
  }
  if (!definition.category || !VALID_CATEGORIES.has(definition.category)) {
    errors.push(`${path}: category is invalid`);
  }
  if (typeof definition.description !== 'string' || !definition.description.trim()) {
    errors.push(`${path}: description is required`);
  }

  return { valid: errors.length === 0, errors };
}

export function validateColorTokenRegistry(
  registry: Record<string, unknown> = colorTokenRegistry,
): ColorTokenRegistryValidation {
  const errors: string[] = [];

  Object.entries(registry).forEach(([name, value]) => {
    if (!isColorTokenName(name)) errors.push(`${name}: token name is invalid`);
    errors.push(...validateColorTokenDefinition(value, name).errors);
  });

  return { valid: errors.length === 0, errors };
}
